import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';

function getHandles(connections: string = 'all', isConnectable?: boolean) {
  const handles: JSX.Element[] = [];
  
  const positions: Array<{ pos: Position; name: string }> = [
    { pos: Position.Top, name: 'top' },
    { pos: Position.Bottom, name: 'bottom' },
    { pos: Position.Left, name: 'left' },
    { pos: Position.Right, name: 'right' },
  ];
  
  positions.forEach(({ pos, name }) => {
    const shouldInclude = 
      connections === 'all' ||
      connections === name ||
      connections === 'top-bottom' && (name === 'top' || name === 'bottom') ||
      connections === 'left-right' && (name === 'left' || name === 'right');
    
    if (shouldInclude) {
      handles.push(
        <Handle
          key={`${name}-source`}
          type="source"
          position={pos}
          id={`${name}-source`}
          isConnectable={isConnectable}
          className="custom-handle"
        />
      );
      handles.push(
        <Handle
          key={`${name}-target`}
          type="target"
          position={pos}
          id={`${name}-target`}
          isConnectable={isConnectable}
          className="custom-handle"
        />
      );
    }
  });
  
  return handles;
}

export const RectangleNode = memo(({ data, isConnectable }: any) => {
  return (
    <div
      style={{
        padding: '10px 15px',
        borderRadius: '4px',
        minWidth: '150px',
        textAlign: 'center',
        backgroundColor: data.style?.backgroundColor || '#1f2937',
        border: `${data.style?.borderWidth || 2}px solid ${data.style?.borderColor || '#9333ea'}`,
        color: '#ffffff',
        fontSize: '12px',
      }}
    >
      {data.label}
      {getHandles(data.connections || 'all', isConnectable)}
    </div>
  );
});

export const CircleNode = memo(({ data, isConnectable }: any) => {
  const bgColor = data.style?.backgroundColor || '#1f2937';
  const borderColor = data.style?.borderColor || '#9333ea';
  const borderWidth = data.style?.borderWidth || 2;
  
  return (
    <div
      style={{
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '5px',
        textAlign: 'center',
        backgroundColor: bgColor,
        border: `${borderWidth}px solid ${borderColor}`,
        color: '#ffffff',
        fontSize: '12px',
        wordBreak: 'break-word',
        overflow: 'hidden',
      }}
    >
      <span style={{ display: 'block', maxWidth: '70px', lineHeight: '1.2' }}>{data.label}</span>
      {getHandles(data.connections || 'all', isConnectable)}
    </div>
  );
});

export const EllipseNode = memo(({ data, isConnectable }: any) => {
  const bgColor = data.style?.backgroundColor || '#1f2937';
  const borderColor = data.style?.borderColor || '#9333ea';
  const borderWidth = data.style?.borderWidth || 2;
  
  return (
    <div
      style={{
        minWidth: '120px',
        minHeight: '60px',
        padding: '10px 20px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        backgroundColor: bgColor,
        border: `${borderWidth}px solid ${borderColor}`,
        color: '#ffffff',
        fontSize: '12px',
      }}
    >
      <span style={{ display: 'block', maxWidth: '70px', lineHeight: '1.2' }}>{data.label}</span>
      {getHandles(data.connections || 'all', isConnectable)}
    </div>
  );
});

export const DiamondNode = memo(({ data, isConnectable }: any) => {
  const bgColor = data.style?.backgroundColor || '#1f2937';
  const borderColor = data.style?.borderColor || '#9333ea';
  const borderWidth = data.style?.borderWidth || 2;
  
  return (
    <div
      style={{
        width: '100px',
        height: '100px',
        position: 'relative',
        background: `linear-gradient(${borderColor}, ${borderColor}) padding-box, linear-gradient(${bgColor}, ${bgColor}) border-box`,
        border: `${borderWidth}px solid transparent`,
        clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          width: `calc(100% - ${borderWidth * 2}px)`,
          height: `calc(100% - ${borderWidth * 2}px)`,
          background: bgColor,
          clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
        }}
      />
      <span style={{ 
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        maxWidth: '60px',
        wordBreak: 'break-word',
        lineHeight: '1.2',
        color: '#ffffff',
        fontSize: '12px',
        zIndex: 1
      }}>
        {data.label}
      </span>
      {getHandles(data.connections || 'all', isConnectable)}
    </div>
  );
});

export const ParallelogramNode = memo(({ data, isConnectable }: any) => {
  const bgColor = data.style?.backgroundColor || '#1f2937';
  const borderColor = data.style?.borderColor || '#9333ea';
  const borderWidth = data.style?.borderWidth || 2;
  
  return (
    <div
      style={{
        padding: '15px 30px',
        minWidth: '120px',
        backgroundColor: bgColor,
        border: `${borderWidth}px solid ${borderColor}`,
        color: '#ffffff',
        fontSize: '12px',
        transform: 'skew(-20deg)',
        textAlign: 'center',
        display: 'inline-block',
      }}
    >
      <span style={{ display: 'block', transform: 'skew(20deg)' }}>{data.label}</span>
      {getHandles(data.connections || 'all', isConnectable)}
    </div>
  );
});
